/**
 * Phase 288 — WhatsApp Template Routing Card (Super Admin)
 *
 * Lets Super Admins rename Meta-approved WhatsApp templates and pause
 * inactive triggers without redeploying. Variables (`{{1}}`, `{{2}}`...)
 * stay code-defined — only template_name + is_active are editable.
 *
 * Wires to:
 *   GET /api/super-admin/whatsapp-mappings
 *   PUT /api/super-admin/whatsapp-mappings
 */
import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Send, Save, RotateCw, AlertTriangle, FlaskConical, Loader2, Download, CheckCircle2, Sparkles, Zap } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

// Phase 288.c — Default placeholder values mirror the backend's
// PHASE288B_PLACEHOLDER_VALUES so the customization form is pre-filled.
// Single source of truth lives on the server; this map exists only so the
// inputs render filled — backend re-applies + validates on submit.
const PHASE288C_DEFAULT_PLACEHOLDERS = {
  name: 'Test User',
  ownerName: 'Test Owner',
  email: 'test@out-play.in',
  tempPassword: 'Test@2026',
  amount: '1000',
  month: 'December 2025',
  transactionId: 'TEST_TX_001',
  receiptUrl: 'https://out-play.in/test/receipt',
  paymentUrl: 'https://out-play.in/test/pay',
  dueDate: '30 Dec 2025',
  role: 'Student',
  academyName: 'Test Academy',
  loginUrl: 'https://out-play.in/login',
  payslipUrl: 'https://out-play.in/test/payslip',
  tierName: 'Pro',
  suspensionDate: '30 Dec 2025',
};

export default function WhatsAppTemplateRoutingCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mappings, setMappings] = useState([]);
  const [dirty, setDirty] = useState(false);
  // Phase 288.b — per-row Send-Test dialog state
  const [testingTrigger, setTestingTrigger] = useState(null);
  const [testPhone, setTestPhone] = useState('');
  const [testRunning, setTestRunning] = useState(false);
  const [testResult, setTestResult] = useState(null);
  // Phase 288.c — custom QA payload override state (per-trigger)
  const [customMode, setCustomMode] = useState(false);
  const [customVars, setCustomVars] = useState({});
  // Phase 644 — Live approved template roster from Meta Business Manager.
  // Populated by clicking "Load from Meta" — once loaded, each row's
  // template_name input becomes a picker with auto language detection.
  const [metaTemplates, setMetaTemplates] = useState([]);
  const [metaTemplatesLoading, setMetaTemplatesLoading] = useState(false);
  const [metaTemplatesError, setMetaTemplatesError] = useState(null);
  const [metaTemplatesLoadedAt, setMetaTemplatesLoadedAt] = useState(null);
  // Phase 645 — Sync-from-Meta flow state
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/super-admin/whatsapp-mappings');
      setMappings(r.data?.mappings || []);
      setDirty(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load mappings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Phase 644 — Fetch the live-approved template roster from Meta Business
  // Manager via the pre-existing /crm-directory/meta-templates endpoint.
  // Only APPROVED templates are returned. Once loaded, each mapping row
  // renders a datalist so the SA picks from real names (typo-proof) and
  // the language auto-fills from Meta's response.
  const loadMetaTemplates = useCallback(async (refresh = false) => {
    setMetaTemplatesLoading(true);
    setMetaTemplatesError(null);
    try {
      const r = await api.get(`/super-admin/crm-directory/meta-templates${refresh ? '?refresh=true' : ''}`);
      const templates = r.data?.templates || [];
      const err = r.data?.error;
      if (err) {
        setMetaTemplates([]);
        setMetaTemplatesError(err);
        toast.error(err);
      } else {
        setMetaTemplates(templates);
        setMetaTemplatesLoadedAt(new Date().toISOString());
        toast.success(`Loaded ${templates.length} approved template${templates.length === 1 ? '' : 's'} from Meta`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to fetch Meta templates';
      setMetaTemplatesError(detail);
      toast.error(detail);
    } finally {
      setMetaTemplatesLoading(false);
    }
  }, []);

  // Phase 644 — Group Meta templates by name (they can exist in multiple
  // languages) so we can (a) show a single suggestion in the picker and
  // (b) auto-select the language on match.
  const metaByName = (() => {
    const out = {};
    for (const t of metaTemplates) {
      if (!t?.name) continue;
      if (!out[t.name]) out[t.name] = [];
      out[t.name].push(t);
    }
    return out;
  })();

  // Phase 645 — Adopt every APPROVED Meta template that isn't yet mapped.
  // Backend POST creates one whatsapp_template_mappings row per orphan,
  // pulling variables from Meta's {{N}} body placeholders and tagging the
  // row `adopted_from_meta: true` so the UI can render a small badge.
  const syncFromMeta = useCallback(async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const r = await api.post('/super-admin/whatsapp-mappings/sync-from-meta');
      const data = r?.data || {};
      setSyncResult(data);
      const created = data.created_count || 0;
      if (created === 0) {
        toast.info(`Nothing to adopt — every approved Meta template is already mapped (${data.skipped_count || 0} skipped).`);
      } else {
        toast.success(`Adopted ${created} new template${created === 1 ? '' : 's'} from Meta — reloading table…`);
        await load();
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Sync failed';
      toast.error(detail);
    } finally {
      setSyncing(false);
    }
  }, [load]);

  const updateRow = (trigger, patch) => {
    setMappings((arr) => arr.map((m) => m.trigger_event === trigger ? { ...m, ...patch } : m));
    setDirty(true);
  };

  // Phase 644 — Called when the SA picks (or types) a template name.
  // If the value matches a known Meta template, auto-fill the language
  // dropdown to the first available language on that template.
  const onTemplateNameChange = (trigger_event, newName) => {
    const patch = { template_name: newName };
    const matches = metaByName[newName?.trim()];
    if (matches && matches.length > 0) {
      // Prefer `en` when available (India-region convention), else first entry
      const preferred = matches.find((t) => (t.language || '').toLowerCase() === 'en') || matches[0];
      if (preferred?.language) {
        patch.language = preferred.language;
      }
    }
    updateRow(trigger_event, patch);
  };

  // Phase 644 — Auto-map: for every mapping row where the CURRENT
  // template_name exists in the Meta approved roster, snap language to
  // whatever Meta approved it under. Batches into a single state update
  // + one toast. Never overwrites the name — only aligns language.
  const autoAlignLanguages = () => {
    if (metaTemplates.length === 0) {
      toast.warning('Load templates from Meta first');
      return;
    }
    let updated = 0;
    setMappings((arr) => arr.map((m) => {
      const matches = metaByName[m.template_name?.trim()];
      if (!matches || matches.length === 0) return m;
      const preferred = matches.find((t) => (t.language || '').toLowerCase() === 'en') || matches[0];
      if (preferred?.language && preferred.language !== m.language) {
        updated++;
        return { ...m, language: preferred.language };
      }
      return m;
    }));
    if (updated > 0) {
      setDirty(true);
      toast.success(`Aligned language on ${updated} row${updated === 1 ? '' : 's'} · click Save to persist`);
    } else {
      toast.info('All templates already aligned to their approved language');
    }
  };

  const save = async () => {
    // Light validation: every row needs a non-empty template_name (even
    // inactive rows — once re-activated, an empty name would crash sends).
    const empty = mappings.find((m) => !m.template_name?.trim());
    if (empty) {
      toast.error(`Template name required for ${empty.humanized_label}`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        mappings: mappings.map((m) => ({
          trigger_event: m.trigger_event,
          template_name: m.template_name.trim(),
          language: (m.language || '').trim(),
          is_active: !!m.is_active,
        })),
      };
      const r = await api.put('/super-admin/whatsapp-mappings', payload);
      toast.success(`Saved ${r.data?.written || 0} mappings · cache refreshed`);
      setDirty(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save mappings');
    } finally {
      setSaving(false);
    }
  };

  const openTestDialog = (trigger) => {
    setTestingTrigger(trigger);
    setTestResult(null);
    setCustomMode(false);
    // Phase 288.c — pre-seed customVars with the backend's placeholder
    // defaults so the operator only has to overwrite the field they care
    // about. Mirrors PHASE288B_PLACEHOLDER_VALUES on the server side.
    setCustomVars(PHASE288C_DEFAULT_PLACEHOLDERS);
    // Don't reset testPhone — let the operator re-use the same number across triggers.
  };

  const runTestSend = async () => {
    const phone = testPhone.trim();
    if (!phone) {
      toast.error('Recipient phone is required (E.164 format, e.g. +919876543210)');
      return;
    }
    if (dirty) {
      toast.warning('Save your mapping changes before testing — current overrides are not yet live.');
      return;
    }
    setTestRunning(true);
    setTestResult(null);
    try {
      // Phase 288.c — only attach custom_payload when the operator
      // explicitly toggled customization on. Empty strings stay empty in
      // the payload — the backend gracefully falls back to defaults for
      // each blank field.
      const reqBody = { to_number: phone };
      if (customMode) {
        const trimmed = {};
        Object.entries(customVars).forEach(([k, v]) => {
          if (typeof v === 'string') trimmed[k] = v;
        });
        reqBody.custom_payload = trimmed;
      }
      const r = await api.post(
        `/super-admin/whatsapp-mappings/${testingTrigger}/send-test`,
        reqBody,
      );
      setTestResult(r.data);
      const result = r.data?.result || {};
      if (result.mock) {
        toast.warning('Meta creds not configured — fired in MOCK mode. Add META_WA_ACCESS_TOKEN + META_WA_PHONE_NUMBER_ID to fire live.');
      } else if (result.success) {
        toast.success(`Test sent · wamid=${(result.wamid || '').slice(0, 20)}…`);
      } else if (result.skipped) {
        toast.warning(`Template is paused — re-activate the row first.`);
      } else {
        toast.error(`Meta rejected: ${result.error || 'unknown error'}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Send test failed');
      setTestResult({ error: err?.response?.data?.detail || 'Send test failed' });
    } finally {
      setTestRunning(false);
    }
  };

  if (loading) {
    return (
      <Card data-testid="wa-template-routing-loading">
        <CardHeader><Skeleton className="h-5 w-56" /></CardHeader>
        <CardContent><Skeleton className="h-48 w-full" /></CardContent>
      </Card>
    );
  }

  const inactiveCount = mappings.filter((m) => !m.is_active).length;

  return (
    <Card className="border-indigo-200 dark:border-indigo-900" data-testid="wa-template-routing-card">
      <CardHeader>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg flex items-center justify-center bg-indigo-100 dark:bg-indigo-950">
              <Send className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <CardTitle className="text-base">Event Template Routing</CardTitle>
              <CardDescription className="text-xs">
                Map platform events → Meta-approved template names. Variables stay code-locked.
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadMetaTemplates(true)}
              disabled={metaTemplatesLoading || saving}
              data-testid="wa-template-routing-fetch-meta-btn"
              title="Fetch APPROVED templates from Meta Business Manager (requires WABA ID in Settings → WhatsApp)"
            >
              {metaTemplatesLoading ? (
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              ) : (
                <Download className="h-3.5 w-3.5 mr-1" />
              )}
              {metaTemplatesLoading ? 'Fetching…' : 'Load from Meta'}
            </Button>
            {metaTemplates.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={autoAlignLanguages}
                disabled={saving}
                data-testid="wa-template-routing-auto-align-btn"
                title="Snap each row's language to whatever Meta approved that template under"
              >
                <Sparkles className="h-3.5 w-3.5 mr-1" /> Auto-align lang
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={syncFromMeta}
              disabled={syncing || saving}
              data-testid="wa-template-routing-sync-from-meta-btn"
              title="Adopt every approved Meta template that isn't already mapped — creates new rows with variables extracted from Meta's template body"
            >
              {syncing ? (
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              ) : (
                <Zap className="h-3.5 w-3.5 mr-1" />
              )}
              {syncing ? 'Syncing…' : 'Sync from Meta'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={saving}
              data-testid="wa-template-routing-reload-btn"
            >
              <RotateCw className="h-3.5 w-3.5 mr-1" /> Reload
            </Button>
            <Button
              onClick={save}
              disabled={!dirty || saving}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="wa-template-routing-save-btn"
            >
              <Save className="h-3.5 w-3.5 mr-1" />
              {saving ? 'Saving…' : 'Save Mappings'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Phase 644 — Meta template roster status banner */}
        {metaTemplatesError && (
          <div
            className="flex items-start gap-2 p-2 rounded-md bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900"
            data-testid="wa-template-routing-meta-error-banner"
          >
            <AlertTriangle className="h-3.5 w-3.5 text-rose-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-rose-800 dark:text-rose-200 leading-relaxed">
              <span className="font-semibold">Meta fetch failed:</span> {metaTemplatesError}
              {metaTemplatesError.toLowerCase().includes('waba') && (
                <> Paste your WABA ID under <span className="font-semibold">Settings → WhatsApp → Meta WhatsApp Cloud → WABA ID</span> and save.</>
              )}
            </p>
          </div>
        )}
        {metaTemplates.length > 0 && !metaTemplatesError && (
          <div
            className="flex items-start gap-2 p-2 rounded-md bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900"
            data-testid="wa-template-routing-meta-loaded-banner"
          >
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-emerald-800 dark:text-emerald-200 leading-relaxed">
              <span className="font-semibold">{metaTemplates.length} approved template{metaTemplates.length === 1 ? '' : 's'}</span>{' '}
              loaded from Meta. Click a template-name input to see live suggestions — picking one auto-fills the correct language.
              {metaTemplatesLoadedAt && (
                <span className="ml-1 opacity-70">
                  · fetched {new Date(metaTemplatesLoadedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </p>
          </div>
        )}

        {/* Phase 645 — Sync-from-Meta result banner (shown after a successful sync) */}
        {syncResult && (
          <div
            className="flex items-start gap-2 p-2 rounded-md bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-900"
            data-testid="wa-template-routing-sync-result-banner"
          >
            <Zap className="h-3.5 w-3.5 text-violet-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-violet-800 dark:text-violet-200 leading-relaxed flex-1">
              <span className="font-semibold">Sync complete:</span>{' '}
              <span data-testid="wa-template-routing-sync-result-created">{syncResult.created_count || 0}</span> adopted,{' '}
              <span data-testid="wa-template-routing-sync-result-skipped">{syncResult.skipped_count || 0}</span> already-mapped.
              {syncResult.created && syncResult.created.length > 0 && (
                <div className="mt-1 opacity-80">
                  New rows: <span className="font-mono">{syncResult.created.join(', ')}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {inactiveCount > 0 && (
          <div
            className="flex items-start gap-2 p-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900"
            data-testid="wa-template-routing-inactive-banner"
          >
            <AlertTriangle className="h-3.5 w-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800 dark:text-amber-200 leading-relaxed">
              <span className="font-semibold">{inactiveCount} template{inactiveCount === 1 ? ' is' : 's are'} paused.</span>{' '}
              The Phase 286 dispatcher will skip them silently (no error, no retry) until you re-toggle them.
            </p>
          </div>
        )}

        <div className="border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="wa-template-routing-table">
              <thead className="bg-muted/40">
                <tr className="text-left">
                  <th className="px-3 py-2 font-medium w-[180px]">Event</th>
                  <th className="px-3 py-2 font-medium">Meta Template Name</th>
                  <th className="px-3 py-2 font-medium w-[110px]">Language</th>
                  <th className="px-3 py-2 font-medium">Required Variables</th>
                  <th className="px-3 py-2 font-medium text-right w-[90px]">Active</th>
                  <th className="px-3 py-2 font-medium text-right w-[110px]">Test</th>
                </tr>
              </thead>
              <tbody>
                {mappings.map((m, idx) => (
                  <tr
                    key={m.trigger_event}
                    className={`border-t align-top hover:bg-muted/20 ${!m.is_active ? 'opacity-60' : ''}`}
                    data-testid={`wa-template-routing-row-${m.trigger_event}`}
                  >
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-800 dark:text-slate-100 flex items-center gap-1.5 flex-wrap">
                        <span>{m.humanized_label}</span>
                        {m.adopted_from_meta && (
                          <span
                            className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-violet-100 dark:bg-violet-950/50 text-violet-700 dark:text-violet-300"
                            title="Adopted from Meta — this template was synced from your WABA approved list (not defined in code registry)"
                            data-testid={`wa-template-routing-adopted-badge-${m.trigger_event}`}
                          >
                            META-ADOPTED
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-muted-foreground font-mono">{m.trigger_event}</div>
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        value={m.template_name}
                        onChange={(e) => onTemplateNameChange(m.trigger_event, e.target.value)}
                        placeholder="meta_approved_template_name"
                        className="h-8 font-mono text-xs"
                        list={metaTemplates.length > 0 ? 'wa-meta-approved-templates' : undefined}
                        data-testid={`wa-template-routing-input-${m.trigger_event}`}
                      />
                      {(() => {
                        // Phase 644 — real-time match badge under the input
                        const matches = metaByName[m.template_name?.trim()];
                        if (metaTemplates.length === 0) return null;
                        if (matches && matches.length > 0) {
                          const langs = [...new Set(matches.map((t) => t.language))];
                          return (
                            <div
                              className="text-[10px] mt-0.5 flex items-center gap-1 text-emerald-700 dark:text-emerald-300"
                              data-testid={`wa-template-routing-match-${m.trigger_event}`}
                            >
                              <CheckCircle2 className="h-2.5 w-2.5" />
                              Approved on Meta · {langs.join(', ')}
                            </div>
                          );
                        }
                        if (m.template_name?.trim()) {
                          return (
                            <div
                              className="text-[10px] mt-0.5 flex items-center gap-1 text-amber-700 dark:text-amber-300"
                              data-testid={`wa-template-routing-nomatch-${m.trigger_event}`}
                            >
                              <AlertTriangle className="h-2.5 w-2.5" />
                              Not found in Meta approved list
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={m.language || m.language_default || 'en_US'}
                        onChange={(e) => updateRow(m.trigger_event, { language: e.target.value })}
                        className="h-8 w-full font-mono text-xs border border-input bg-background rounded-md px-2"
                        data-testid={`wa-template-routing-language-${m.trigger_event}`}
                        title={`Code default: ${m.language_default || 'en_US'}`}
                      >
                        <option value="en">en</option>
                        <option value="en_US">en_US</option>
                        <option value="en_GB">en_GB</option>
                        <option value="en_IN">en_IN</option>
                        <option value="hi">hi</option>
                        <option value="hi_IN">hi_IN</option>
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-[10px] text-slate-500 dark:text-slate-400">
                        Requires:{' '}
                        {(m.required_variables || []).map((v, i) => (
                          <span key={v} className="font-mono">
                            <span className="text-slate-400">{`{{${i + 1}}}`}</span>{' '}
                            <span className="text-slate-700 dark:text-slate-300">{v}</span>
                            {i < m.required_variables.length - 1 ? ', ' : ''}
                          </span>
                        ))}
                      </div>
                      {m.updated_by && (
                        <div className="text-[10px] text-slate-400 dark:text-slate-500 mt-1 italic">
                          Last edit: {m.updated_at ? new Date(m.updated_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Switch
                        checked={!!m.is_active}
                        onCheckedChange={(v) => updateRow(m.trigger_event, { is_active: v })}
                        data-testid={`wa-template-routing-toggle-${m.trigger_event}`}
                        aria-label={`Toggle ${m.humanized_label}`}
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-[10px]"
                        onClick={() => openTestDialog(m.trigger_event)}
                        disabled={!m.is_active}
                        title={!m.is_active ? 'Activate the row first' : 'Fire a live Meta send with placeholder vars'}
                        data-testid={`wa-template-routing-test-btn-${m.trigger_event}`}
                      >
                        <FlaskConical className="h-3 w-3 mr-1" /> Test
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {dirty && (
          <p
            className="text-[11px] text-amber-700 dark:text-amber-300"
            data-testid="wa-template-routing-dirty-hint"
          >
            ● You have unsaved changes. Click <span className="font-semibold">Save Mappings</span> to apply them.
          </p>
        )}

        {/* Phase 644 — Shared datalist referenced by every template_name Input.
            Browser renders it as a native autocomplete dropdown on focus. */}
        {metaTemplates.length > 0 && (
          <datalist id="wa-meta-approved-templates" data-testid="wa-meta-approved-datalist">
            {Object.keys(metaByName).sort().map((name) => {
              const entries = metaByName[name];
              const langs = [...new Set(entries.map((t) => t.language))].join(', ');
              return <option key={name} value={name} label={`${langs}`} />;
            })}
          </datalist>
        )}
      </CardContent>

      {/* Phase 288.b — Send-Test dialog (one phone input, autofilled placeholder vars) */}
      <Dialog open={!!testingTrigger} onOpenChange={(o) => { if (!o) { setTestingTrigger(null); setTestResult(null); } }}>
        <DialogContent data-testid="wa-template-routing-test-dialog">
          <DialogHeader>
            <DialogTitle>Send test for {testingTrigger?.replace(/_/g, ' ')}</DialogTitle>
            <DialogDescription>
              Fires a live Meta Graph API call using the configured template name plus auto-filled placeholder values (e.g., name = "Test User", amount = "1000"). Use a number you own — this WILL deliver a real WhatsApp message.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Recipient phone (E.164)</Label>
              <Input
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="+919876543210"
                className="font-mono text-sm"
                data-testid="wa-template-routing-test-phone-input"
              />
            </div>

            {/* Phase 288.c — Custom QA Payload Injector */}
            {(() => {
              const triggerRow = mappings.find((m) => m.trigger_event === testingTrigger);
              const vars = triggerRow?.required_variables || [];
              return (
                <div
                  className="border rounded-md p-2.5 bg-muted/20"
                  data-testid="wa-template-routing-custom-mode-block"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <Label className="text-xs font-medium">Customize Test Variables</Label>
                      <p className="text-[10px] text-muted-foreground leading-snug">
                        Override defaults to QA the actual wording parents will see (e.g., "₹4,500" instead of "1000").
                        Blank fields fall back to defaults.
                      </p>
                    </div>
                    <Switch
                      checked={customMode}
                      onCheckedChange={setCustomMode}
                      data-testid="wa-template-routing-custom-mode-toggle"
                      aria-label="Toggle custom variables"
                    />
                  </div>
                  {customMode && vars.length > 0 && (
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {vars.map((varName, idx) => (
                        <div key={varName} className="space-y-0.5">
                          <Label className="text-[10px] font-mono text-slate-500 dark:text-slate-400">
                            {`{{${idx + 1}}}`} {varName}
                          </Label>
                          <Input
                            value={customVars[varName] ?? ''}
                            onChange={(e) =>
                              setCustomVars((prev) => ({ ...prev, [varName]: e.target.value }))
                            }
                            placeholder={PHASE288C_DEFAULT_PLACEHOLDERS[varName] ?? `[TEST ${varName}]`}
                            className="text-sm py-1 h-8"
                            data-testid={`wa-template-routing-custom-var-${varName}`}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {testResult && (
              <div
                className="rounded-md border p-3 bg-muted/30"
                data-testid="wa-template-routing-test-result"
              >
                <p className="text-xs font-semibold mb-1">Result</p>
                <pre className="text-[10px] font-mono whitespace-pre-wrap break-all max-h-48 overflow-auto">
                  {JSON.stringify(testResult.result || testResult, null, 2)}
                </pre>
                {testResult.variables_sent && (
                  <details className="mt-2" open={(testResult.overrides_applied || []).length > 0}>
                    <summary className="text-[10px] text-muted-foreground cursor-pointer">
                      Variables sent
                      {(testResult.overrides_applied || []).length > 0 && (
                        <span className="ml-1 text-indigo-600 font-semibold">
                          ({testResult.overrides_applied.length} override{testResult.overrides_applied.length === 1 ? '' : 's'} applied)
                        </span>
                      )}
                    </summary>
                    <pre className="text-[10px] font-mono mt-1 opacity-70">
                      {JSON.stringify(testResult.variables_sent, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setTestingTrigger(null); setTestResult(null); }}
              data-testid="wa-template-routing-test-close-btn"
            >
              Close
            </Button>
            <Button
              onClick={runTestSend}
              disabled={testRunning || !testPhone.trim()}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="wa-template-routing-test-send-btn"
            >
              {testRunning ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
              {testRunning ? 'Sending…' : 'Send Test'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
